import { MapPin, Store, Utensils, Compass, Phone, Clock, Edit2 } from 'lucide-react';

const ViewPartnerDetails = ({ partner, onEdit }) => {
  if (!partner) return null;

  const getIcon = (category) => {
    switch (category) {
      case 'Restaurant':
        return <Utensils size={24} className="text-[#067978]" />;
      case 'Shop':
        return <Store size={24} className="text-[#067978]" />;
      case 'Activity':
        return <Compass size={24} className="text-[#067978]" />;
      default:
        return <Store size={24} className="text-[#067978]" />;
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 w-full mx-auto animate-tab-switch">
      {/* Header Actions */}
      {onEdit && (
        <div className="flex justify-end">
          <button
            onClick={() => onEdit(partner)}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-[#067978] hover:bg-[#056362] text-white rounded-2xl font-bold text-xs sm:text-sm shadow-md shadow-[#067978]/20 transition-all active:scale-95 w-full sm:w-auto cursor-pointer"
          >
            <Edit2 size={16} /> Edit Partner
          </button>
        </div>
      )}

      {/* Main Details Card */}
      <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-100 p-4 sm:p-6 md:p-8 shadow-sm space-y-6 sm:space-y-8">
        {/* Profile Header */}
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-5 pb-6 border-b border-slate-100">
          <div className="h-16 w-16 sm:h-20 sm:w-20 bg-emerald-50/60 rounded-2xl border border-emerald-100/60 flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
            {partner.logo_url ? (
              <img src={partner.logo_url} alt="Logo" className="w-full h-full object-cover" />
            ) : (
              getIcon(partner.category)
            )}
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="px-2.5 py-0.5 rounded-full bg-teal-50 text-[#067978] border border-teal-100/70 text-[10px] sm:text-xs font-medium capitalize">
                {partner.category
                  ? partner.category.charAt(0).toUpperCase() + partner.category.slice(1).toLowerCase()
                  : 'Shop'}
              </span>
              {partner.isActive !== false ? (
                <span className="px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 text-[10px] sm:text-xs font-bold uppercase tracking-wide">
                  Active
                </span>
              ) : (
                <span className="px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-500 border border-rose-100 text-[10px] sm:text-xs font-bold uppercase tracking-wide">
                  Frozen
                </span>
              )}
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800">
              {partner.business_name}
            </h2>
            <p className="text-xs sm:text-sm font-semibold text-slate-500 flex items-center gap-1.5 pt-0.5">
              <MapPin size={16} className="text-slate-400" />{' '}
              {partner.city || 'Location unavailable'}
            </p>
          </div>
        </div>

        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-slate-50 p-4 sm:p-5 rounded-2xl border border-slate-100">
            <p className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
              Owner Name
            </p>
            <p className="font-bold text-base sm:text-lg text-slate-800">
              {partner.owner_name || 'N/A'}
            </p>
          </div>
          <div className="bg-slate-50 p-4 sm:p-5 rounded-2xl border border-slate-100">
            <p className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
              Contact Phone
            </p>
            <p className="font-bold text-base sm:text-lg text-slate-800 flex items-center gap-2">
              <Phone size={18} className="text-slate-400" />
              {partner.phone_number
                ? partner.country_code
                  ? `${partner.country_code} ${partner.phone_number}`
                  : partner.phone_number
                : partner.user_id?.phone_number || 'N/A'}
            </p>
          </div>
        </div>

        {/* Address */}
        <div>
          <p className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">
            Full Address
          </p>
          <p className="text-xs sm:text-sm font-medium text-slate-700 bg-slate-50 p-4 sm:p-5 rounded-2xl border border-slate-100 leading-relaxed">
            {partner.address}
            <br />
            {partner.city}, {partner.pincode}
          </p>
        </div>

        {/* Description */}
        {partner.description && (
          <div>
            <p className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">
              Description
            </p>
            <p className="text-xs sm:text-sm font-medium text-slate-700 bg-slate-50 p-4 sm:p-5 rounded-2xl border border-slate-100 whitespace-pre-wrap leading-relaxed">
              {partner.description}
            </p>
          </div>
        )}

        {/* Hours & Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <p className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
              <Clock size={16} /> Business Hours
            </p>
            <div className="bg-slate-50 p-4 sm:p-5 rounded-2xl border border-slate-100 space-y-3">
              <div className="flex gap-1.5 flex-wrap">
                {partner.businessHours?.days?.map((d) => (
                  <span
                    key={d}
                    className="px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700"
                  >
                    {d}
                  </span>
                ))}
                {(!partner.businessHours?.days || partner.businessHours.days.length === 0) && (
                  <span className="text-xs sm:text-sm text-slate-500">Not specified</span>
                )}
              </div>
              {partner.businessHours?.openingTime && (
                <p className="text-xs sm:text-sm font-bold text-slate-800 mt-2">
                  {partner.businessHours.openingTime} - {partner.businessHours.closingTime}
                </p>
              )}
            </div>
          </div>

          {partner.category === 'Restaurant' && (
            <div>
              <p className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">
                Price for Two
              </p>
              <div className="bg-slate-50 p-4 sm:p-5 rounded-2xl border border-slate-100">
                <p className="text-lg sm:text-xl font-bold text-slate-800">
                  ₹{partner.price_for_two || 0}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Gallery */}
        {partner.image_url && partner.image_url.length > 0 && (
          <div>
            <p className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
              Media Gallery
            </p>
            <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-2">
              {partner.image_url.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt={`Gallery ${i}`}
                  className="h-28 w-28 sm:h-32 sm:w-32 object-cover rounded-2xl border border-slate-200 shrink-0 shadow-sm"
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewPartnerDetails;
